import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { ANALYTICS } from '../../analytics/constants';
import { PayloadProps, track } from '../../analytics/index';
import { QuickTransferIcon } from '../../assets/svg';
import { DateFilter } from '../../components/table/components/date-filter';
import { ViewWrapper } from '../../components/view-wrapper';
import { CONSTANTS } from '../../config/constants';
import { useMerchant } from '../../hooks/useMerchant';
import { Filters } from '../../models/table';
import { QuickTransferServices } from '../../services/quick-transfer';
import { defaultFilters, defaultPagination } from '../../utils/defaults';
import { QuickTransferHeader } from './components/header';
import { QuickTransferTable } from './components/table';
import { HeadContainer, TableFilterHeader, TitlePage } from './styles';

const QuickTransfer = () => {
	const { merchantSelected } = useMerchant();
	const countryMX =
		merchantSelected?.country === 'MX'
	const conciliationOnlySuccess =
		merchantSelected?.products_config?.filter_by_section?.conciliation?.state_selected;
	const voucherPdf =
		merchantSelected?.products_config?.filter_by_section?.quick_transfer?.voucher_pdf;

	const customDefaultFilters: Filters = {
		...defaultFilters,
		stateSelected: conciliationOnlySuccess ? 'success' : '',
	};

	const [filters, setFilters] = useState(customDefaultFilters);
	const [pagination, setPagination] = useState(defaultPagination);
	const [total, setTotal] = useState(0);

	const { data: graphs, isLoading: isLoadingGraph } = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		queryKey: [CONSTANTS.services.quickTransfer.getGraphData, merchantSelected?.code],
		queryFn: () => QuickTransferServices.getGraphicsData(merchantSelected?.code as string),
	});

	const {
		data: table,
		isFetching,
		isFetched,
		isLoading: isLoadingTable,
	} = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [
			CONSTANTS.services.quickTransfer.getTableInfo,
			merchantSelected?.code,
			pagination,
			filters,
		],
		queryFn: () =>
			QuickTransferServices.getAllPayments({
				merchantCode: merchantSelected?.code as string,
				pagination,
				filters: {
					...filters,
					dateRange: {
						from: filters?.dateRange?.from,
						to: filters?.dateRange?.to,
					},
				},
			}),
		onSuccess: (data) => {
			if (data) setTotal(data.total);
		},
	});

	useEffect(() => {
		setFilters(customDefaultFilters);
		setPagination(defaultPagination);
	}, [merchantSelected]);

	useEffect(() => {
		const trackInfo: PayloadProps = {
			type: 'screen',
			pageView: ANALYTICS.quick_transfer.page_view,
			source: 'GTM',
		};
		track(trackInfo);
	}, []);

	const TitlePageLabel = countryMX ? 'Botón de cobro' : 'Quick Transfer';

	return (
		<ViewWrapper title={TitlePageLabel} isLoading={isLoadingTable || isLoadingTable || isFetching}>
			<HeadContainer>
				<TitlePage>
					<QuickTransferIcon /> {TitlePageLabel}
				</TitlePage>
			</HeadContainer>

			<QuickTransferHeader data={graphs} loading={isLoadingGraph} countryMX={countryMX} />

			<TableFilterHeader>
				<DateFilter
					filters={filters}
					onFilterChange={setFilters}
					pagination={pagination}
					setPagination={setPagination}
				/>
			</TableFilterHeader>

			<QuickTransferTable
				data={table?.tableInfo || []}
				isLoading={isFetching && !isFetched}
				stateList={table?.typeOfStatus || []}
				filters={filters}
				onFilterChange={setFilters}
				pagination={pagination}
				setPagination={setPagination}
				totalResults={total}
				conciliationOnlySuccess={conciliationOnlySuccess}
				voucherPdf={voucherPdf || graphs?.hasCash}
			/>
		</ViewWrapper>
	);
};

export { QuickTransfer };